import { DataDate } from "../model/data_date";
import { Series, SeriesSet } from "../model/series";
import { tablemill } from "../tablemill_metal";
import { CookStatsGraphForm } from "../view/cook_stats_graph_form";
import { RelatedRecipesController } from "./related_recipes_controller";
import $ from "jquery";

class CookTrendsController {
  private form: Form;
  private view: any;
  private relatedRecipesController: RelatedRecipesController;

  constructor() {
    this.form = new Form();
    this.view = new CookStatsGraphForm();
    this.view.initialize(this.form.invalidParameterCombinations());
    this.relatedRecipesController = new RelatedRecipesController();
    this.relatedRecipesController.initialize();
  }

  onLoad(): void {
    if (!this.form.isKeywordFound()) {
      this.view.showKeywordNotFoundError(this.view.keywordValue());
      this.relatedRecipesController.view.showNotFoundMessage();
      return;
    }
    this.renderGraph();
    this.relatedRecipesController.render();
  }

  private renderGraph(): void {
    const query = new ItemDataSeriesQuery(this.form);
    query.execute().then(
      (seriesSet: any) => {
        if (seriesSet.hasNoData()) {
          this.view.showNoDataError();
        } else {
          this.view.renderGraph(seriesSet);
        }
      },
      (res: any) => {
        this.view.showHttpError(res);
      },
    );
  }
}

class Form {
  ajaxUrl(): string {
    return $("#si_graph").data("ajaxUrl");
  }

  type(): string | number | string[] | undefined {
    return $("#type").val();
  }

  partition(): string | number | string[] | undefined {
    return $("#partition").val();
  }

  isKeywordFound(): boolean {
    return !$("#keyword").data("notFound");
  }

  targetDataDate(): any {
    return DataDate.parseYMD(
      $("#target").val()?.toString(),
      this.partition()?.toString(),
    );
  }

  dimension(): string | number | string[] | undefined {
    return $("#dimension").val();
  }

  area(): string | number | string[] | undefined {
    return $("#area").val();
  }

  category(): string | number | string[] | undefined {
    return $("#category").val();
  }

  invalidParameterCombinations(): any {
    return $("#cook_trends_form").data("invalidParameterCombinations");
  }
}

class ItemDataSeriesQuery {
  private url: string;
  private type: string;
  private partition: string;
  private target: any;
  private dimension: string;
  private area: string;
  private category: string;

  constructor(ctx: Form) {
    this.url = ctx.ajaxUrl();
    this.type = ctx.type() as string;
    this.partition = ctx.partition() as string;
    this.target = ctx.targetDataDate();
    this.dimension = ctx.dimension() as string;
    this.area = ctx.area() as string;
    this.category = ctx.category() as string;
  }

  private isYearly(): boolean {
    return this.partition === "yearly";
  }

  execute(): JQuery.Promise<any> {
    if (this.isYearly()) {
      return $.ajax({
        url: this.url,
        data: this.makeYearlyParams(),
        dataType: "json",
      }).then((response: any) => {
        const r = this.wholeAvailableDateRange();
        return new SeriesSet([
          new Series(response.values, this.seriesName(r.start, r.end), r.start),
        ]);
      });
    } else {
      return $.ajax({
        url: this.url,
        data: this.makeParams(),
        dataType: "json",
      }).then((response: any) => {
        return this.splitSeries(response.values, this.dateRange());
      });
    }
  }

  private makeYearlyParams(): any {
    const r = this.wholeAvailableDateRange();
    return {
      type: this.type,
      partition: this.partition,
      start: r.start.ymd(),
      end: r.end.ymd(),
      dimension: this.dimension,
      area: this.area,
      category: this.category,
    };
  }

  private wholeAvailableDateRange(): any {
    return {
      start: new DataDate(tablemill.FIRST_DATA_DATE, this.partition),
      end: this.target.partitionStartDate(),
    };
  }

  private makeParams(): any {
    const r = this.dateRange();
    return {
      type: this.type,
      partition: this.partition,
      start: r.start.ymd(),
      end: r.end.ymd(),
      dimension: this.dimension,
      area: this.area,
      category: this.category,
    };
  }

  private dateRange(): any {
    const end = this.target.partitionStartDate();
    const start2 = end.halfYearAgo();
    const start = start2.yearsAgo(1);
    return { start, start2, end };
  }

  private splitSeries(values: any[], dates: any): any {
    const threshold = dates.start2.toDate();
    const grouped = values.reduce(
      (acc, [d, s]) => {
        const isPrev = DataDate.getDateForYMD(d) < threshold;
        if (!acc[isPrev ? "true" : "false"]) {
          acc[isPrev ? "true" : "false"] = [];
        }
        acc[isPrev ? "true" : "false"].push([d, s]);
        return acc;
      },
      { true: [], false: [] },
    );

    return new SeriesSet([
      new Series(
        grouped.true,
        this.seriesName(dates.start, dates.start2),
        dates.start,
      ),
      new Series(
        grouped.false,
        this.seriesName(dates.start2, dates.end),
        dates.start2,
      ),
    ]);
  }

  private seriesName(begin: any, end: any): string {
    return `${begin.year()}/${begin.month()} - ${end.year()}/${end.month()}`;
  }
}

export { CookTrendsController };
